import React, { useState, useEffect } from 'react';
import jsPDF from 'jspdf';
import { X, Download, Share } from 'lucide-react';
import { db } from '../firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';

const OrderSheetGenerator = ({ order, companyDetails: propCompanyDetails, onClose }) => {
  const [inventory, setInventory] = useState([]);
  const [orders, setOrders] = useState([]);
  const [companyDetails, setCompanyDetails] = useState({
    name: 'ADDICT',
    address: '123 Business Street',
    city: 'Colombo, Sri Lanka',
    phone: '+94 11 234 5678',
    email: 'info@addict.lk'
  });
  
  useEffect(() => {
    loadData();
    // Load company details from localStorage or use props
    const storedCompany = localStorage.getItem('addictCompany');
    if (storedCompany) {
      setCompanyDetails(JSON.parse(storedCompany));
    } else if (propCompanyDetails) {
      setCompanyDetails(propCompanyDetails);
    }
  }, [propCompanyDetails]);
  
  const loadData = async () => {
    try {
      const [inventorySnapshot, ordersSnapshot] = await Promise.all([
        getDocs(collection(db, 'inventory')),
        getDocs(collection(db, 'orders'))
      ]);
      
      const inventoryData = inventorySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const ordersData = ordersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      setInventory(inventoryData);
      setOrders(ordersData);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };
  
  const getStockStatus = (articleNumber) => {
    const product = inventory.find(p => p.articleNumber === articleNumber);
    if (!product) return { status: 'Unknown', available: 0, reserved: 0 };
    
    const totalPairs = (product.packsInStock * product.pairsPerPack) + (product.loosePairs || 0);
    
    // Calculate reserved quantity
    let reservedPairs = 0;
    orders.forEach(orderData => {
      if (orderData.status !== 'completed' && orderData.items) {
        orderData.items.forEach(item => {
          if (item.articleNumber === articleNumber && item.productionStatus !== 'completed') {
            reservedPairs += item.totalPairs || 0;
          }
        });
      }
    });
    
    const availablePairs = totalPairs - reservedPairs;
    
    // Check if this specific order item needs production
    const currentOrderItem = order.items.find(item => item.articleNumber === articleNumber);
    const needsProduction = currentOrderItem && (currentOrderItem.stockStatus === 'out-of-stock' || availablePairs <= 0);
    
    return {
      status: needsProduction ? 'Production Required' : availablePairs > 0 ? 'Available' : 'Reserved',
      available: availablePairs,
      reserved: reservedPairs,
      total: totalPairs,
      needsProduction
    };
  };
  
  const generateOrderSheet = () => {
    const doc = new jsPDF();
    
    // Load and add logo
    const addLogo = () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = function() {
          canvas.width = img.width;
          canvas.height = img.height;
          ctx.drawImage(img, 0, 0);
          const dataURL = canvas.toDataURL('image/jpeg');
          doc.addImage(dataURL, 'JPEG', 170, 8, 25, 25);
        };
        img.src = window.location.origin + '/logo.jpeg';
      } catch (error) {
        console.log('Logo loading failed:', error);
      }
    };
    
    // Modern header with gradient effect
    doc.setFillColor(102, 126, 234);
    doc.rect(0, 0, 210, 40, 'F');
    
    // Company header in white with custom font
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text(companyDetails.name || 'ADDICT', 15, 20);
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'italic');
    doc.text('its more than an addiction', 15, 28);
    
    // Add logo
    addLogo();
    
    // Company details in white box
    doc.setFillColor(255, 255, 255);
    doc.rect(130, 8, 70, 24, 'F');
    doc.setTextColor(102, 126, 234);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text(companyDetails.address || '123 Business Street', 135, 15);
    doc.text(companyDetails.phone || '+94 11 234 5678', 135, 20);
    doc.text(companyDetails.email || 'info@addict.lk', 135, 25);
    
    // Reset text color
    doc.setTextColor(0, 0, 0);
    
    // Title
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('ORDER SHEET', 15, 50);
    
    // Order details box
    doc.setFillColor(248, 249, 250);
    doc.rect(15, 55, 180, 20, 'F');
    doc.setDrawColor(102, 126, 234);
    doc.setLineWidth(0.5);
    doc.rect(15, 55, 180, 20);
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const orderNumber = String(order.id).slice(-6).padStart(6, '0');
    doc.text(`Order #: ${orderNumber}`, 20, 62);
    doc.text(`Customer: ${order.customerName}`, 20, 69);
    doc.text(`Date: ${new Date(order.createdAt).toLocaleDateString()}`, 120, 62);
    doc.text(`Status: ${order.status.toUpperCase()}`, 120, 69);
    
    // Table header with modern styling
    let yPos = 85;
    doc.setFillColor(102, 126, 234);
    doc.rect(15, yPos - 5, 180, 10, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.text('ARTICLE', 20, yPos);
    doc.text('COLOUR', 45, yPos);
    doc.text('PACKS', 70, yPos);
    doc.text('PAIRS', 90, yPos);
    doc.text('NOTE', 110, yPos);
    doc.text('STOCK STATUS', 140, yPos);
    doc.text('PRODUCTION', 175, yPos);
    
    yPos += 8;
    doc.setTextColor(0, 0, 0);
    
    // Items
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    
    order.items.forEach((item, index) => {
      if (yPos > 270) {
        doc.addPage();
        yPos = 30;
      }
      
      const stockInfo = getStockStatus(item.articleNumber);
      const product = inventory.find(p => p.articleNumber === item.articleNumber);
      const packsNeeded = Math.ceil(item.totalPairs / (product?.pairsPerPack || 1));
      
      // Alternating row colors
      const bgColor = index % 2 === 0 ? [255, 255, 255] : [248, 249, 250];
      doc.setFillColor(...bgColor);
      doc.rect(15, yPos - 4, 180, 8, 'F');
      
      doc.text(item.articleNumber, 20, yPos);
      doc.text(item.colour, 45, yPos);
      doc.text(packsNeeded.toString(), 70, yPos);
      doc.text(item.totalPairs.toString(), 90, yPos);
      doc.text(item.note || '-', 110, yPos);
      
      // Stock status with reservation info
      const statusText = stockInfo.reserved > 0 
        ? `${stockInfo.status} (R:${stockInfo.reserved})` 
        : stockInfo.status;
      doc.text(statusText, 140, yPos);
      
      doc.text(item.productionStatus === 'completed' ? 'DONE' : 'PENDING', 175, yPos);
      
      yPos += 8;
    });
    
    // Modern footer
    yPos += 10;
    doc.setDrawColor(102, 126, 234);
    doc.setLineWidth(1);
    doc.line(15, yPos, 195, yPos);
    yPos += 8;
    doc.setFontSize(7);
    doc.setTextColor(102, 102, 102);
    doc.text(`Generated: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`, 15, yPos);
    
    return doc;
  };
  
  const handleDownload = () => {
    const doc = generateOrderSheet();
    const orderNumber = String(order.id).slice(-6).padStart(6, '0');
    doc.save(`Production-Order-${orderNumber}.pdf`);
  };
  
  const handleWhatsAppShare = () => {
    const doc = generateOrderSheet();
    const pdfBlob = doc.output('blob');
    
    const url = URL.createObjectURL(pdfBlob);
    const orderNumber = String(order.id).slice(-6).padStart(6, '0');
    const message = `Production Order Sheet\\nOrder #: ${orderNumber}\\nCustomer: ${order.customerName}\\nItems: ${order.items.length} products\\nStatus: ${order.status.toUpperCase()}`;
    const whatsappUrl = `https://web.whatsapp.com/send?text=${encodeURIComponent(message)}`;
    
    window.open(whatsappUrl, '_blank');
    
    setTimeout(() => {
      const link = document.createElement('a');
      link.href = url;
      link.download = `Production-Order-${orderNumber}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    }, 1000);
  };
  
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '90vw', width: '600px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 className="modal-title">Order Sheet - #{String(order.id).slice(-6).padStart(6, '0')}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
            <X size={24} />
          </button>
        </div>
        
        <div style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', padding: '15px', borderRadius: '12px', marginBottom: '20px', color: 'white' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px' }}>
            <div><strong>Customer:</strong> {order.customerName}</div>
            <div><strong>Order #:</strong> #{String(order.id).slice(-6).padStart(6, '0')}</div>
            <div><strong>Date:</strong> {new Date(order.createdAt).toLocaleDateString()}</div>
            <div><strong>Status:</strong> {order.status.toUpperCase()}</div>
          </div>
        </div>
        
        <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '12px', marginBottom: '20px', overflowX: 'auto' }}>
          <h4 style={{ marginBottom: '15px', color: '#333' }}>Production Items</h4>
          <div style={{ minWidth: '500px' }}>
            {order.items.map((item, index) => {
              const stockInfo = getStockStatus(item.articleNumber);
              const product = inventory.find(p => p.articleNumber === item.articleNumber);
              const packsNeeded = Math.ceil(item.totalPairs / (product?.pairsPerPack || 1));
              return (
                <div key={index} style={{ 
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 60px 60px 1fr 120px 80px',
                  gap: '8px',
                  alignItems: 'center',
                  padding: '10px',
                  marginBottom: '8px',
                  background: 'white',
                  borderRadius: '8px',
                  border: '1px solid #e9ecef',
                  fontSize: '0.85rem'
                }}>
                  <div><strong>{item.articleNumber}</strong></div>
                  <div>{item.colour}</div>
                  <div>{packsNeeded}</div>
                  <div>{item.totalPairs}</div>
                  <div style={{ fontSize: '0.8rem', color: '#666' }}>{item.note || '-'}</div>
                  <div style={{ fontSize: '0.8rem' }}>
                    <div style={{ color: stockInfo.status === 'Available' ? '#27ae60' : stockInfo.status === 'Reserved' ? '#f39c12' : '#e74c3c' }}>
                      {stockInfo.status}
                    </div>
                    {stockInfo.reserved > 0 && (
                      <div style={{ color: '#f39c12', fontSize: '0.75rem' }}>Reserved: {stockInfo.reserved}</div>
                    )}
                    <div style={{ color: '#666', fontSize: '0.75rem' }}>Available: {stockInfo.available}</div>
                  </div>
                  <div style={{ 
                    padding: '2px 8px', 
                    borderRadius: '12px', 
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    textAlign: 'center',
                    background: item.productionStatus === 'completed' ? '#d4edda' : '#fff3cd',
                    color: item.productionStatus === 'completed' ? '#155724' : '#856404'
                  }}>
                    {item.productionStatus === 'completed' ? 'DONE' : 'PENDING'}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button 
            onClick={handleDownload}
            className="btn btn-primary"
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <Download size={16} />
            Download Order Sheet
          </button>
          <button 
            onClick={handleWhatsAppShare}
            className="btn btn-secondary"
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              background: '#25D366',
              color: 'white'
            }}
          >
            <Share size={16} />
            Share on WhatsApp
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderSheetGenerator;